import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type { CaixaDeNotificacoes, FiltroDaCaixa, PreferenciasDeNotificacao } from './contrato';

/**
 * Camada de dados da caixa de notificações e das preferências. Dado real, da
 * API (`bff/v1/notificacoes`): cada pessoa lê e grava só o que é dela, pela
 * política de linha do banco (0042) sobre o usuário do token.
 *
 * Uma fonte só para as três telas que mostram avisos — a central de cada área,
 * o sino do cabeçalho e o portal —, para que marcar como lida numa delas apareça
 * nas outras: toda escrita invalida a raiz `CHAVES.tudo`.
 */
export const CAMINHO_DA_API = '/bff/v1/notificacoes';

export const CHAVES = {
  tudo: ['notificacoes'] as const,
  caixa: (filtro: FiltroDaCaixa) => ['notificacoes', 'caixa', filtro] as const,
  preferencias: ['notificacoes', 'preferencias'] as const,
};

export function consultaDaCaixa(filtro: FiltroDaCaixa): string {
  const parametros = new URLSearchParams();
  parametros.set('pagina', String(filtro.pagina));
  parametros.set('tamanho', String(filtro.tamanho));
  if (filtro.categoria) parametros.set('categoria', filtro.categoria);
  if (filtro.tipo) parametros.set('tipo', filtro.tipo);
  if (filtro.prioridade) parametros.set('prioridade', filtro.prioridade);
  if (filtro.somenteNaoLidas) parametros.set('somenteNaoLidas', 'true');
  const busca = filtro.busca?.trim();
  if (busca) parametros.set('busca', busca);
  if (filtro.criadaDe) parametros.set('criadaDe', filtro.criadaDe);
  if (filtro.criadaAte) parametros.set('criadaAte', filtro.criadaAte);
  return `${CAMINHO_DA_API}?${parametros.toString()}`;
}

export function useCaixaDeNotificacoes(
  filtro: FiltroDaCaixa,
  opcoes: { habilitada?: boolean; atualizarACadaMs?: number } = {},
) {
  return useQuery({
    queryKey: CHAVES.caixa(filtro),
    queryFn: () => chamarApi<CaixaDeNotificacoes>(consultaDaCaixa(filtro)),
    enabled: opcoes.habilitada ?? true,
    refetchInterval: opcoes.atualizarACadaMs,
    // A página seguinte mantém a anterior na tela enquanto chega.
    placeholderData: (anterior) => anterior,
    retry: naoInsistirEmRecusa,
  });
}

function useEscritaNaCaixa<V, R>(executar: (v: V) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [...CHAVES.tudo, 'caixa'] });
    },
  });
}

export function useMarcarComoLida() {
  return useEscritaNaCaixa((id: string) =>
    chamarApi<{ id: string; lidaEm: string | null }>(
      `${CAMINHO_DA_API}/${encodeURIComponent(id)}/lida`,
      { metodo: 'POST' },
    ),
  );
}

export function useMarcarTodasComoLidas() {
  return useEscritaNaCaixa(() =>
    chamarApi<{ marcadas: number }>(`${CAMINHO_DA_API}/lidas`, { metodo: 'POST' }),
  );
}

export function usePreferenciasDeNotificacao() {
  return useQuery({
    queryKey: CHAVES.preferencias,
    queryFn: () => chamarApi<PreferenciasDeNotificacao>(`${CAMINHO_DA_API}/preferencias`),
    retry: naoInsistirEmRecusa,
  });
}

export function useSalvarPreferenciasDeNotificacao() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: boolean }) =>
      chamarApi<PreferenciasDeNotificacao>(`${CAMINHO_DA_API}/preferencias`, {
        metodo: 'PUT',
        corpo: v,
      }),
    onSuccess: (gravadas) => {
      cliente.setQueryData(CHAVES.preferencias, gravadas);
    },
  });
}

/** Mensagem da API quando ela disse o que fazer; senão, a genérica. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}
